import type { Dispatch, SetStateAction } from 'react';
import React, { useEffect } from 'react';
import { NoSsr, Flex } from '@withjoy/joykit';
import { Shell } from '@apps/guest/packages/layout-engine/layouts/LayoutBrannan/components/Shell';
import { Footer } from '@apps/guest/packages/layout-engine/components/Footer';
import { brannanFooterContainerStyles } from '@apps/guest/packages/layout-engine/components/Footer/Footer.styles';
import { useLayoutBrannanController } from '@apps/guest/packages/layout-engine/layouts/LayoutBrannan/LayoutBrannan.controller';
import { useBrannanDesign } from '@apps/guest/packages/layout-engine/layouts/LayoutBrannan/LayoutBrannan.theme';
import { useScrollToTop } from '@shared/components/ScrollToTop/ScrollToTop';
import type { GuestSiteEvent } from '@apps/guest/packages/layout-engine/layouts/layout.types';
import type { Maybe } from '@graphql/generated-base';
import { CashFundGuestContent } from './CashFundGuestContent';
import { useCashFundsTelemetry } from '../../../CashFunds.telemetry';
import { useMount } from '@shared/utils/hooks/useMount';

interface CashFundGuestLayoutProps {
  readonly fundId: string;
  readonly event: GuestSiteEvent;
  readonly setLoadingTheme?: Maybe<Dispatch<SetStateAction<boolean>>>;
}

export const CashFundGuestLayout: React.FC<CashFundGuestLayoutProps> = ({ fundId, event, setLoadingTheme }) => {
  const { pages } = event;
  const { handleFooterButtonClicked } = useLayoutBrannanController(pages);
  const telemetry = useCashFundsTelemetry();

  const { loading } = useBrannanDesign(event.website, event.eventDesign);

  useMount(() => {
    telemetry.cashFundGuest.enter(fundId);
  });

  useEffect(() => {
    if (setLoadingTheme) {
      setLoadingTheme(loading);
    }
  }, [loading, setLoadingTheme]);

  useScrollToTop({ behavior: 'instant' });

  const shellKey = event.eventDesign?.theme.themeId || 'default';

  return (
    <Shell
      eventDesign={event.eventDesign}
      isCustomPage={true}
      key={shellKey}
      styleApplicatorLoading={loading}
    >
      <NoSsr>
        <CashFundGuestContent
          fundId={fundId}
          event={event}
        />
        <Flex __css={brannanFooterContainerStyles}>
          <Footer
            onButtonClicked={handleFooterButtonClicked}
            data-testid={'cashfund-guest-footer'}
          />
        </Flex>
      </NoSsr>
    </Shell>
  );
};
